import { useState } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/utils";
import { FlaskConical, Dna, Gem, Pill, FileSearch, ArrowRight, Copy, Check, Wrench, Database, BookOpen } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { type MockScienceAgent, DOMAIN_CONFIG } from "@/data/mockScienceAgents";

const DOMAIN_ICONS: Record<string, (s: number) => React.ReactNode> = {
  flask: (s) => <FlaskConical size={s} />,
  dna: (s) => <Dna size={s} />,
  gem: (s) => <Gem size={s} />,
  pill: (s) => <Pill size={s} />,
  "file-search": (s) => <FileSearch size={s} />,
};

export function getDomainIcon(icon: string, size = 20) {
  return (DOMAIN_ICONS[icon] || DOMAIN_ICONS.flask)(size);
}

export default function AgentCard({ agent }: { agent: MockScienceAgent }) {
  const [copied, setCopied] = useState(false);
  const { language } = useLanguage();
  const config = DOMAIN_CONFIG[agent.domain];

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(agent.agent_key);
    if (ok) {
      setCopied(true);
      toast.success(language === "zh" ? "已复制" : "Copied");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="sci-card p-4 hover:border-white/15 transition-colors">
      {/* Row 1: Icon + Name + Domain badge + Copy */}
      <div className="flex items-center gap-3 mb-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border"
          style={{ background: `${config.color}15`, borderColor: `${config.color}30`, color: config.color }}
        >
          {getDomainIcon(config.icon, 16)}
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-sm font-semibold text-white truncate">
            {language === "zh" ? agent.display_name : agent.display_name_en}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/8 flex-shrink-0">
            {language === "zh" ? "领域模型" : "Domain"}
          </span>
        </div>
        <button onClick={handleCopy} className="p-1 rounded text-slate-600 hover:text-slate-300 hover:bg-white/5 flex-shrink-0">
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
        </button>
      </div>

      {/* Row 2: Stats */}
      <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Wrench size={10} /> {agent.stats.tool_count} {language === "zh" ? "工具" : "Tools"}</span>
        <span className="flex items-center gap-1"><Database size={10} /> {agent.stats.datasource_count} {language === "zh" ? "数据源" : "Sources"}</span>
        <span className="flex items-center gap-1"><BookOpen size={10} /> {agent.stats.scenario_count} {language === "zh" ? "场景" : "Scenarios"}</span>
      </div>

      {/* Row 3: Description */}
      <p className="text-[11px] text-slate-500 truncate mb-2.5">
        {language === "zh" ? agent.tagline : agent.tagline_en}
      </p>

      {/* Row 4: Tags + status */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {(language === "zh" ? agent.capability_tags : agent.capability_tags_en).slice(0, 4).map((tag) => (
          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/8">
            {tag}
          </span>
        ))}
        {agent.capability_tags.length > 4 && (
          <span className="text-[9px] text-slate-600">+{agent.capability_tags.length - 4}</span>
        )}
      </div>

      {/* Row 5: model_key + CTA */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/3 border border-white/8 min-w-0">
          <code className="text-[10px] font-mono text-slate-500 truncate">{agent.agent_key}</code>
        </div>
        <Link href={`/console/science-agents/${agent.agent_key}`}>
          <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: `linear-gradient(135deg, ${config.color}, ${config.color}bb)` }}>
            {language === "zh" ? "详情" : "Details"} <ArrowRight size={11} />
          </button>
        </Link>
      </div>
    </div>
  );
}
